import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { AssessmentShell, ScaleQuestion } from "@/components/built-index/AssessmentShell";
import { BUILT_INDEX_ROUTES, BUILT_PRINCIPLES, type BuiltKey } from "@/content/built-index";
import { readSession, writeSession } from "@/lib/built-index-session";
import { trackBuiltIndexStep } from "@/lib/built-index-analytics";

const BuiltIndexDiagnostic = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Partial<Record<BuiltKey, number>>>({});

  useEffect(() => {
    const s = readSession();
    if (!s.id) {
      navigate(BUILT_INDEX_ROUTES.start, { replace: true });
      return;
    }
    setAnswers(s.built);
  }, [navigate]);

  const set = (key: BuiltKey, value: number) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    writeSession({ built: next });
  };

  const answered = BUILT_PRINCIPLES.filter((p) => typeof answers[p.key] === "number").length;
  const complete = answered === BUILT_PRINCIPLES.length;

  return (
    <>
      <PageMeta
        title="The Built Diagnostic™ · The Built Index"
        description="Rate yourself across the ten MAKECHANGE leadership principles."
        path="/built-index/diagnostic"
        noindex
      />
      <AssessmentShell
        step={1}
        title="The Built Diagnostic™"
        intro="Rate each principle from 1 to 10 based on the last 90 days — not your intentions. Honest numbers produce a useful report."
        footer={
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <span className="text-silver text-xs uppercase tracking-[0.2em]">
              {answered} of {BUILT_PRINCIPLES.length} answered
            </span>
            <button
              type="button"
              disabled={!complete}
              onClick={() => {
                trackBuiltIndexStep("assessment_built", {
                  sessionId: readSession().id,
                  email: readSession().email,
                  metadata: { answered },
                });
                navigate(BUILT_INDEX_ROUTES.disc);
              }}
              className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition disabled:opacity-50"
            >
              Continue to Leadership Style <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        }
      >
        {BUILT_PRINCIPLES.map((p) => (
          <ScaleQuestion
            key={p.key}
            id={p.key}
            label={`${p.letter} · ${p.name}`}
            prompt={p.prompt}
            value={answers[p.key]}
            onChange={(v) => set(p.key, v)}
          />
        ))}
        <p className="text-silver/60 text-xs">
          Need to stop? Your progress is saved on this device.{" "}
          <Link to={BUILT_INDEX_ROUTES.landing} className="text-gold underline">
            Back to overview
          </Link>
        </p>
      </AssessmentShell>
    </>
  );
};

export default BuiltIndexDiagnostic;
